<template>
  <div class="viq-lens-selector">
    <div v-if="lensesLoading" class="viq-lens-selector__loading">
      <div class="viq-lens-selector__skeleton" v-for="i in 3" :key="i" />
    </div>

    <template v-else>
      <!-- Clear active lens -->
      <div v-if="activeLens" class="viq-lens-selector__clear">
        <button class="viq-lens-selector__clear-btn" @click="clearLens">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
          </svg>
          Clear lens
        </button>
      </div>

      <!-- VIXIO LENSES -->
      <div class="viq-lens-selector__group">
        <div class="viq-lens-selector__group-header">
          <svg class="viq-lens-selector__group-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="1" y="1" width="10" height="3" rx="1" fill="currentColor" opacity="0.6" />
            <rect x="1" y="5.5" width="10" height="3" rx="1" fill="currentColor" opacity="0.4" />
            <rect x="1" y="8" width="10" height="3" rx="1" fill="currentColor" opacity="0.2" />
          </svg>
          <span class="viq-lens-selector__group-label">VIXIO LENSES</span>
        </div>

        <div
          v-for="tpl in templateLenses"
          :key="tpl.id"
          class="viq-lens-selector__item"
          :class="{ 'viq-lens-selector__item--active': activeLens && activeLens.id === tpl.id }"
          @click="selectLens(tpl)"
        >
          <ViqLensIcon :value="tpl.emoji || tpl.id" :size="16" class="viq-lens-selector__item-icon" />
          <div class="viq-lens-selector__item-info">
            <span class="viq-lens-selector__item-name">{{ tpl.name }}</span>
            <span class="viq-lens-selector__item-desc">{{ tpl.description }}</span>
          </div>
        </div>
      </div>

      <!-- MY LENSES -->
      <div class="viq-lens-selector__group">
        <div class="viq-lens-selector__group-header">
          <span class="viq-lens-selector__group-label">MY LENSES</span>
        </div>

        <template v-if="userLenses.length > 0">
          <div
            v-for="lens in userLenses"
            :key="lens.id"
            class="viq-lens-selector__item"
            :class="{ 'viq-lens-selector__item--active': activeLens && activeLens.id === lens.id }"
            @click="selectLens(lens)"
          >
            <ViqLensIcon :value="lens.emoji || lens.id" :size="16" class="viq-lens-selector__item-icon" />
            <div class="viq-lens-selector__item-info">
              <span class="viq-lens-selector__item-name">{{ lens.name }}</span>
              <span v-if="lens.description" class="viq-lens-selector__item-desc">{{ lens.description }}</span>
            </div>
            <button
              class="viq-lens-selector__delete"
              title="Delete lens"
              @click.stop="deleteLens(lens.id)"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </template>

        <div v-else class="viq-lens-selector__empty">
          No custom lenses yet
        </div>

        <button v-if="mode !== 'lens-build'" class="viq-lens-selector__create-btn" @click="openLensBuilder">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
          </svg>
          Create custom lens
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqLensSelector",
  components: {
    ViqLensIcon: () => import("./ViqLensIcon"),
  },
  computed: {
    ...mapState("Viq", ["lenses", "activeLens", "lensesLoading", "mode"]),
    templateLenses() {
      if (!this.lenses) return [];
      return this.lenses.filter((l) => l.is_template || l.isTemplate);
    },
    userLenses() {
      if (!this.lenses) return [];
      return this.lenses.filter((l) => !l.is_template && !l.isTemplate);
    },
  },
  methods: {
    selectLens(lens) {
      this.$store.commit("Viq/SET_ACTIVE_LENS", lens);
    },
    clearLens() {
      this.$store.commit("Viq/SET_ACTIVE_LENS", null);
    },
    deleteLens(id) {
      this.$store.dispatch("Viq/deleteLens", id);
    },
    openLensBuilder() {
      this.$store.commit("Viq/SET_MODE", "lens-build");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-lens-selector {
  &__clear {
    padding: 0 $viq-space-sm $viq-space-sm;
  }

  &__clear-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: $viq-space-xs $viq-space-sm;
    border: 1px dashed $viq-border;
    border-radius: $viq-radius-sm;
    background: none;
    color: $viq-text-muted;
    font-size: $viq-font-xs;
    cursor: pointer;
    transition: color $viq-transition-fast, border-color $viq-transition-fast;

    &:hover {
      color: $viq-error;
      border-color: $viq-error;
    }
  }

  &__loading {
    padding: 0 $viq-space-xs;
  }

  &__skeleton {
    height: 48px;
    background: $viq-bg-secondary;
    border-radius: $viq-radius-sm;
    margin-bottom: $viq-space-xs;
    animation: pulse 1.5s ease-in-out infinite;
  }

  &__group {
    margin-bottom: $viq-space-md;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__group-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: $viq-space-xs $viq-space-sm;
    margin-bottom: $viq-space-xs;
  }

  &__group-icon {
    color: $viq-accent;
    flex-shrink: 0;
  }

  &__group-label {
    font-size: 10px;
    font-weight: 700;
    color: $viq-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: $viq-space-sm;
    padding: $viq-space-sm 10px;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-bg-secondary;

      .viq-lens-selector__delete {
        opacity: 1;
      }
    }

    &--active {
      background: $viq-accent-light;
    }
  }

  &__item-icon {
    color: $viq-accent;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item-name {
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-text-primary;
    line-height: 1.3;
  }

  &__item-desc {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
    line-height: 1.4;
  }

  &__delete {
    opacity: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    color: $viq-text-muted;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    flex-shrink: 0;
    margin-top: 2px;
    transition: opacity $viq-transition-fast, color $viq-transition-fast;

    &:hover {
      color: $viq-error;
    }
  }

  &__empty {
    padding: $viq-space-sm 10px;
    font-size: $viq-font-xs;
    color: $viq-text-muted;
    font-style: italic;
  }

  &__create-btn {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    width: 100%;
    padding: $viq-space-sm 10px;
    margin-top: $viq-space-xs;
    border: none;
    background: none;
    color: $viq-accent;
    font-size: $viq-font-sm;
    font-weight: 600;
    cursor: pointer;
    border-radius: $viq-radius-md;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-accent-light;
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
